// Components
import ColorSelector from '@components/ColorSelector/ColorSelector';
import { ENABLE_SOCIAL_LINKS_CUSTOM_COLOR } from '@components/EmailSignaturev2/consts';
import {
  EmailSignatureFillStyle,
  EmailSignatureShapes,
  EmailSignatureSizes,
  EmailSignatureSocialColors,
  SideEditorNodeKey,
} from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
// types
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import { faFacebookF, faFacebookSquare } from '@fortawesome/free-brands-svg-icons';
import { faCircle, faSquareFull } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// hooks and context
import useNode from '@hooks/use-node';
// utils
// es-toolkit/compat: lodash isEmpty treats 0/true/null as empty
import { isEmpty } from 'es-toolkit/compat';
import { type FC, useContext, useEffect } from 'react';

import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
  EmailSignatureEditorPanelSectionHeadingButton,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import EmailSignatureSquircle from '../EmailSignatureSquircle';
import styles from './EmailSignatureEditorDesignPanelSection.module.scss';

const EmailSignatureEditorSocialLinksDesignSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { scrollIntoView, registerNode, panelIndex } = useSideEditorContext();
  const { isEverythingEmpty, emailSignature, emailSignatureDispatch, isSignatureLocked } =
    useContext(emailSignatureSideEditorServicesContext);
  const [socialLinksDesignSectionDiv, socialLinksDesignSectionDivRef] = useNode<HTMLDivElement>();

  useEffect(() => {
    if (!socialLinksDesignSectionDiv) return;
    registerNode(SideEditorNodeKey.SOCIAL_LINKS_DESIGN_NODE, {
      panelBelongTo: panelIndex,
      node: socialLinksDesignSectionDiv,
    });
  }, [socialLinksDesignSectionDiv, registerNode, panelIndex]);

  const disabledInputs =
    (isEmpty(emailSignature.socialLinks) && !isEverythingEmpty) || isSignatureLocked;

  return (
    <EmailSignatureEditorPanelSection ref={socialLinksDesignSectionDivRef}>
      <EmailSignatureEditorPanelSectionHeading heading={'Links'}>
        {!isSignatureLocked && (
          <EmailSignatureEditorPanelSectionHeadingButton
            buttonText={`${isEmpty(emailSignature.socialLinks) ? 'Add' : 'Edit'} links`}
            onClick={() => {
              scrollIntoView(SideEditorNodeKey.SOCIAL_LINKS_NODE, SideEditorScrollType.START);
            }}
            isEmphasised={isEmpty(emailSignature.socialLinks) && !isEverythingEmpty}
          />
        )}
      </EmailSignatureEditorPanelSectionHeading>
      <EmailSignatureEditorPanelSectionContents
        // only disable the section when the fields are empty, we will show a different ui when the section is locked
        disabled={isEmpty(emailSignature.socialLinks) && !isEverythingEmpty}
      >
        <div className={styles['design-section-option']} data-direction="vertical">
          <div className={styles['design-section-option-label']}>Style</div>
          <FormSelectorGroup
            className={styles['design-section-group-icons-selector']}
            selectedValue={
              emailSignature.design.socialLinksFillStyle ?? EmailSignatureFillStyle.FULL
            }
            size="sm"
            onChange={(newValue) =>
              emailSignatureDispatch({
                type: 'UPDATE_DESIGN_SOCIAL_FILL_STYLE',
                value: newValue as EmailSignatureFillStyle,
              })
            }
          >
            <div className={styles['design-section-form-icon-selector']}>
              <FormSelector disabled={disabledInputs} value={EmailSignatureFillStyle.FULL}>
                <FontAwesomeIcon icon={faFacebookSquare} />
              </FormSelector>
            </div>
            <div className={styles['design-section-form-icon-selector']}>
              <FormSelector disabled={disabledInputs} value={EmailSignatureFillStyle.LIGHT}>
                <FontAwesomeIcon icon={faFacebookF} />
              </FormSelector>
            </div>
          </FormSelectorGroup>
        </div>

        <div className={styles['design-section-option']} data-direction="vertical">
          <div className={styles['design-section-option-label']}>Shape</div>
          <FormSelectorGroup
            className={styles['design-section-group-icons-selector']}
            selectedValue={emailSignature.design.socialLinksShape ?? EmailSignatureShapes.CIRCLE}
            size="sm"
            onChange={(newValue) =>
              emailSignatureDispatch({
                type: 'UPDATE_DESIGN_SOCIAL_SHAPE',
                value: newValue as EmailSignatureShapes,
              })
            }
          >
            <div className={styles['design-section-form-icon-selector']}>
              <FormSelector disabled={disabledInputs} value={EmailSignatureShapes.CIRCLE}>
                <FontAwesomeIcon icon={faCircle}></FontAwesomeIcon>
              </FormSelector>
            </div>
            <div className={styles['design-section-form-icon-selector']}>
              <FormSelector disabled={disabledInputs} value={EmailSignatureShapes.SQUARE}>
                <FontAwesomeIcon icon={faSquareFull}></FontAwesomeIcon>
              </FormSelector>
            </div>
            <div className={styles['design-section-form-icon-selector']}>
              <FormSelector disabled={disabledInputs} value={EmailSignatureShapes.SQUIRCLE}>
                <EmailSignatureSquircle />
              </FormSelector>
            </div>
          </FormSelectorGroup>
        </div>

        <div className={styles['design-section-option']} data-direction="vertical">
          <div className={styles['design-section-option-label']}>Size</div>
          <FormSelectorGroup
            className={styles['design-section-group-form-selector']}
            selectedValue={emailSignature.design.socialLinksSize ?? EmailSignatureSizes.MEDIUM}
            size="sm"
            onChange={(newValue) =>
              emailSignatureDispatch({
                type: 'UPDATE_DESIGN_SOCIAL_SIZE',
                value: newValue as EmailSignatureSizes,
              })
            }
          >
            <div className={styles['design-section-form-selector']}>
              <FormSelector
                disabled={disabledInputs}
                value={EmailSignatureSizes.SMALL}
                className={styles['design-section-form-selector']}
              >
                Small
              </FormSelector>
            </div>

            <div className={styles['design-section-form-selector']}>
              <FormSelector
                disabled={disabledInputs}
                value={EmailSignatureSizes.MEDIUM}
                className={styles['design-section-form-selector']}
              >
                Medium
              </FormSelector>
            </div>

            <div className={styles['design-section-form-selector']}>
              <FormSelector
                disabled={disabledInputs}
                value={EmailSignatureSizes.LARGE}
                className={styles['design-section-form-selector']}
              >
                Large
              </FormSelector>
            </div>
          </FormSelectorGroup>
        </div>

        <div className={styles['design-section-option']} data-direction="vertical">
          <div className={styles['design-section-option-label']}>Icons color</div>
          <div className={styles['design-section-option-multi-steps']}>
            <FormSelectorGroup
              className={styles['design-section-group-form-selector']}
              selectedValue={emailSignature.design.socialLinksColorMode}
              size="sm"
              onChange={(newValue) =>
                emailSignatureDispatch({
                  type: 'UPDATE_DESIGN_SOCIAL_COLOR_MODE',
                  value: newValue as EmailSignatureSocialColors,
                })
              }
            >
              <div className={styles['design-section-form-selector']}>
                <FormSelector
                  disabled={disabledInputs}
                  value={EmailSignatureSocialColors.THEME}
                  className={styles['design-section-form-selector']}
                >
                  Theme Color
                </FormSelector>
              </div>
              <div className={styles['design-section-form-selector']}>
                <FormSelector
                  disabled={disabledInputs}
                  value={EmailSignatureSocialColors.BRAND}
                  className={styles['design-section-form-selector']}
                >
                  Brand
                </FormSelector>
              </div>
              {ENABLE_SOCIAL_LINKS_CUSTOM_COLOR && (
                <div className={styles['design-section-form-selector']}>
                  <FormSelector
                    disabled={disabledInputs}
                    value={EmailSignatureSocialColors.CUSTOM}
                    className={styles['design-section-form-selector']}
                  >
                    Custom
                  </FormSelector>
                </div>
              )}
            </FormSelectorGroup>
            {emailSignature.design.socialLinksColorMode === EmailSignatureSocialColors.CUSTOM && (
              <ColorSelector
                color={emailSignature.design.socialLinksColor}
                onSelect={(color) =>
                  emailSignatureDispatch({
                    type: 'UPDATE_DESIGN_SOCIAL_COLOR',
                    value: color,
                  })
                }
                disabled={disabledInputs}
              />
            )}
          </div>
        </div>
        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit this styling, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorPanelSectionContents>
    </EmailSignatureEditorPanelSection>
  );
};

export default EmailSignatureEditorSocialLinksDesignSection;
